/**
 * PREVIEW MODE — how a superdemo Test Venue shows its setup (owner, 2026-10-28).
 * ============================================================================
 *
 * Until now a showcase venue (`Business.demo_locked`, set by superdemo / prm014) padlocked
 * twelve of its dashboard blocks: a visitor saw a lock where the product should be. Now every
 * block opens its REAL dialog, filled with the venue's real setup, in a look-don't-touch frame:
 * fields greyed and inert, Save refused with a toast, Close / tabs / scrolling still live, and a
 * banner "Demo venue · Look around freely, changes are off" with a "Start your own" button.
 *
 * ⚠️ ONLY for showcase venues. The pages decide (`isShowcase` = Business.demo_locked && not
 * demo maintenance) and call `openInPreview()` instead of opening the dialog directly; nothing
 * here ever turns on by itself, so a real business can never land in preview.
 *
 * How a dialog becomes a preview without touching the dialog itself: `openInPreview(fn)` arms a
 * short window, then `fn()` opens the dialog; the guard (components/demo/PreviewModeGuard.vue)
 * watches <body>, and the `.q-dialog` that appears inside that window is tagged
 * `data-dd-preview`. A dialog opened while a preview dialog is already up (a sub-dialog) is
 * tagged too. A whole PAGE can opt in by carrying `data-dd-preview` on its root element.
 *
 * ⚠️ A COURTESY layer, like utils/testAccount.ts. The real lock is the server's demo guard
 * (DD_Parse_Server cloud/demoGuard.js), which refuses setup writes from the demo login.
 */
import { ref } from 'vue'

/** Selector for anything in preview — a tagged dialog or an opted-in page. */
export const PREVIEW_SELECTOR = '[data-dd-preview]'

const ARM_MS = 1500
let armedUntil = 0

/** True while at least one preview dialog/page is on screen (the guard keeps it current). */
export const previewActive = ref(false)

/** Is a dialog that appears right now meant to open in preview? Read by the guard. */
export function isPreviewArmed(): boolean {
  return Date.now() < armedUntil
}

/** The guard calls this once the armed dialog has appeared, so the window is used exactly once
 *  (a booking dialog opened a second later on the mainboard must open normally). */
export function disarmPreview(): void {
  armedUntil = 0
}

/** Open a dialog in preview: arm the window, then run the opener. */
export function openInPreview(open: () => void): void {
  armedUntil = Date.now() + ARM_MS
  open()
}

/**
 * The one call the pages use: on a showcase venue open in preview, otherwise open normally.
 *   @click="openSetup(isShowcase, () => showCoworkersDialog = true)"
 */
export function openSetup(showcase: boolean, open: () => void): void {
  if (showcase) openInPreview(open)
  else open()
}
